import {
    CustomerDetails,
    SearchCriteria,
    TravellerDetails,
    TravellerValidation
} from '@/src/types/visa/visaItinerary'
import { isDateAfter, isEmpty } from '@/src/utils/helper'

// Calculate age from date of birth based on dateOfVisa
const calculateAge = (dateOfBirth: string, dateOfVisa: string): number => {
    const birthDate = new Date(dateOfBirth)
    const visaDate = new Date(dateOfVisa)

    let age = visaDate.getFullYear() - birthDate.getFullYear()
    const monthDifference = visaDate.getMonth() - birthDate.getMonth()

    // Adjust age if birthday hasn't occurred yet by visa date
    if (
        monthDifference < 0 ||
        (monthDifference === 0 && visaDate.getDate() < birthDate.getDate())
    ) {
        age--
    }

    return age
}

export const validateTravellerInItinerary = (
    traveller: TravellerDetails,
    contactDetails: {
        isdCode: string
        phoneNumber: string
        customerEmail: string
    },
    validations: TravellerValidation,
    searchCriteria: SearchCriteria,
    allTravellers?: TravellerDetails[],
    currentTravellerIndex?: number
) => {
    const errorFields: Record<string, string> = {}
    const { isPrimary, passportDetails, personalDetails } = traveller
    const { passportNumber, expiryDate } = passportDetails
    const { firstName, lastName, dateOfBirth, gender, paxType } =
        personalDetails
    const { phoneNumber, customerEmail, isdCode } = contactDetails
    const { dateOfVisa } = searchCriteria

    // Helper function to validate a field
    const validateField = (
        fieldName: string,
        value: string,
        errorMessage: string,
        validationFn?: () => boolean
    ) => {
        if (isEmpty(value)) {
            errorFields[fieldName] = errorMessage
            return false
        }

        if (validationFn && !validationFn()) {
            errorFields[fieldName] = errorMessage
            return false
        }

        return true
    }

    // Validate primary traveller contact details
    if (isPrimary) {
        validateField(
            'phoneNumber',
            phoneNumber && isdCode ? 'valid' : '',
            'Please enter a valid phone number',
            () => {
                const { maxLength, minLength, regex } = validations.phoneNumber
                return (
                    !isEmpty(phoneNumber) &&
                    !isEmpty(isdCode) &&
                    phoneNumber.length >= minLength &&
                    phoneNumber.length <= maxLength &&
                    new RegExp(regex).test(phoneNumber)
                )
            }
        )

        validateField(
            'customerEmail',
            customerEmail,
            'Please enter a valid email',
            () =>
                new RegExp(validations.customerEmail.regex).test(customerEmail)
        )
    }

    // Validate personal details
    validateField(
        'firstName',
        firstName,
        'Please enter a valid first name',
        () => new RegExp(validations.firstName.regex).test(firstName)
    )

    validateField('lastName', lastName, 'Please enter a valid last name', () =>
        new RegExp(validations.lastName.regex).test(lastName)
    )

    // Validate date of birth with age check based on paxType
    validateField(
        'dateOfBirth',
        dateOfBirth,
        paxType === 'ADULT'
            ? 'Adult age must be more than 12 years'
            : 'Child age must be between 1 and 18 years',
        () => {
            if (isEmpty(paxType) || isEmpty(dateOfVisa)) return true

            const age = calculateAge(dateOfBirth, dateOfVisa)
            if (paxType === 'ADULT') {
                return age >= 13
            } else if (paxType === 'CHILD') {
                return age <= 12
            }
            return true
        }
    )

    validateField('gender', gender, 'Gender is required')

    validateField(
        'passportNumber',
        passportNumber,
        passportNumber.length === 0
            ? 'Passport number is required'
            : 'Passport number should be between 6-9 characters',
        () => {
            if (passportNumber.length >= 6 && passportNumber.length <= 9)
                return true
            else return false
        }
    )

    // Validate passport number uniqueness within the same trip
    if (!isEmpty(passportNumber) && allTravellers && currentTravellerIndex !== undefined) {
        const isDuplicatePassport = allTravellers.some((otherTraveller, index) => {
            if (index === currentTravellerIndex) return false
            return otherTraveller.passportDetails.passportNumber?.toLowerCase() === passportNumber.toLowerCase()
        })
        
        if (isDuplicatePassport) {
            errorFields['passportNumber'] = 'Passport already used'
        }
    }

    // Validate passport expiry date is after date of arrival
    validateField(
        'expiryDate',
        expiryDate,
        'Passport must be valid on date of arrival',
        () => {
            if (isEmpty(dateOfVisa)) return true
            return isDateAfter(expiryDate, dateOfVisa)
        }
    )

    return {
        isTravellerValid: Object.keys(errorFields).length === 0,
        errorFields
    }
}

export const getUpdateItineraryPayload = (
    customerDetails: CustomerDetails,
    itineraryId: string
) => {
    const { travellerDetails, isdCode, phoneNumber, customerEmail, username } =
        customerDetails
    const payload = {
        itineraryId,
        customerDetails: {
            username: username || customerEmail,
            customerEmail,
            phoneNumber,
            isdCode,
            travellerDetails
        }
    }
    return payload
}
